import React, { useContext } from "react";
import {
  Box,
  Container,
  Typography,
  Link,
  Divider,
  useTheme,
  useMediaQuery,
  IconButton,
  Stack,
  alpha,
} from "@mui/material";
import Grid from "@mui/material/Grid"; // Grid v2 import
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { COMPANY_INFO } from "../../utils/constants";
import { motion } from "framer-motion"; // Framer Motion'ı import edin (npm install framer-motion)
import { TransitionContext } from "../../context/TransitionContext"; // TransitionContext'i import edin

// Icons
import LocationOnIcon from "@mui/icons-material/LocationOn";
import EmailIcon from "@mui/icons-material/Email";
import PhoneIcon from "@mui/icons-material/Phone";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import FacebookIcon from "@mui/icons-material/Facebook";
import TwitterIcon from "@mui/icons-material/Twitter";
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import InstagramIcon from "@mui/icons-material/Instagram";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";

// Sosyal medya renkleri
const socialColors = {
  Facebook: "#3b5998",
  Twitter: "#1da1f2",
  LinkedIn: "#0077b5",
  Instagram: "#e1306c",
};

// Başlık rengi (turuncu)
const headerColor = "#f39c12";

const Footer = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const isSmall = useMediaQuery(theme.breakpoints.down("sm"));
  // TransitionContext'i kullan
  const { startTransition } = useContext(TransitionContext);

  const currentYear = new Date().getFullYear();

  // Products items
  const productItemsColumn1 = [
    {
      name: { tr: "Arpa", en: "Barley" },
      path: "/products/barley",
    },
    {
      name: { tr: "Buğday", en: "Wheat" },
      path: "/products/wheat",
    },
    {
      name: { tr: "Mısır", en: "Corn" },
      path: "/products/corn",
    },
    {
      name: { tr: "Mısır Yağı", en: "Corn Oil" },
      path: "/products/corn-oil",
    },
  ];

  const productItemsColumn2 = [
    {
      name: { tr: "Ayçiçek Yağı", en: "Sunflower Oil" },
      path: "/products/sunflower-oil",
    },
    {
      name: { tr: "Tavuk", en: "Chicken" },
      path: "/products/chicken",
    },
    {
      name: { tr: "Gübre", en: "Fertilizer" },
      path: "/products/fertilizer",
    },
  ];

  // Navigation Links - Column 1 (Sayfalar)
  const navLinks1 = [
    { name: t("nav.home"), path: "/" },
    { name: t("nav.about"), path: "/about" },
    { name: t("nav.contact"), path: "/contact" },
  ];

  // Social media items
  const socialMedia = [
    { icon: <FacebookIcon />, name: "Facebook", url: "#" },
    { icon: <TwitterIcon />, name: "Twitter", url: "#" },
    { icon: <LinkedInIcon />, name: "LinkedIn", url: "#" },
    { icon: <InstagramIcon />, name: "Instagram", url: "#" },
  ];

  // Modern sayfa geçişi ve yönlendirme
  const handleNavigate = (path) => {
    // Önce animasyonla sayfadan çıkış, sonra navigate
    startTransition(() => {
      navigate(path);
      // scrollTo işlemi TransitionContext tarafından yönetilecek
    });
  };

  // Görsel dosya yolu
  const bgImage = "/images/1920_500wheatfield.jpg";

  return (
    <>
      {/* Main Footer */}
      <Box
        sx={{
          position: "relative",
          color: "white",
          pt: { xs: 5, md: 6 },
          pb: 2,
          mt: "auto",
          boxShadow: "0 -10px 20px rgba(0,0,0,0.1)",
          bgcolor: "#2c3e50",
          backgroundImage: `linear-gradient(175deg, rgba(44, 62, 80, 0.5) 0%, rgba(44, 62, 80, 0.85) 100%), url(${bgImage})`,
          backgroundSize: "cover",
          backgroundPosition: "center bottom",
          backgroundRepeat: "no-repeat",
        }}
      >
        <Container maxWidth="xl" sx={{ px: { xs: 3, sm: 4 } }}>
          <Grid
            container
            spacing={{ xs: 3, md: 4 }}
            justifyContent="space-between"
            sx={{ position: "relative", zIndex: 1 }}
          >
            {/* Logo ve şirket ismi - Mobil Optimize */}
            <Grid
              item
              xs={12}
              sm={6}
              md={3}
              sx={{
                textAlign: { xs: 'center', sm: 'left' }
              }}
            >
              <Box
                component={motion.div}
                whileHover={{ scale: 1.03 }}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  mb: 2,
                  cursor: "pointer",
                  justifyContent: { xs: 'center', sm: 'flex-start' }
                }}
                onClick={() => handleNavigate("/")}
              >
                <Box
                  component="img"
                  src="/images/sgt_logo.png"
                  alt="SGT Logo"
                  sx={{ height: { xs: 45, sm: 50 }, mr: 2 }}
                />
                <Typography
                  variant="h6"
                  component="div"
                  sx={{
                    fontWeight: 700,
                    color: "white",
                    letterSpacing: 0.5,
                    lineHeight: 1.2,
                    fontSize: { xs: '1.1rem', sm: '1.25rem' }
                  }}
                >
                  SAFE GLOBAL TRADE
                </Typography>
              </Box>

              <Typography
                variant="body2"
                mb={3}
                sx={{
                  lineHeight: 1.7,
                  color: alpha("#fff", 0.8),
                  maxWidth: { xs: "100%", sm: 280 },
                  px: { xs: 2, sm: 0 },
                  textAlign: { xs: 'center', sm: 'left' }
                }}
              >
                {i18n.language === "tr"
                  ? COMPANY_INFO.tagline.tr
                  : COMPANY_INFO.tagline.en}
              </Typography>

              <Stack 
                direction="row" 
                spacing={1.5} 
                sx={{ 
                  mt: 2,
                  justifyContent: { xs: 'center', sm: 'flex-start' }
                }}
              >
                {socialMedia.map((social) => (
                  <IconButton
                    key={social.name}
                    href={social.url}
                    target="_blank"
                    rel="noopener"
                    size="small"
                    component={motion.button}
                    whileHover={{ 
                      y: -5,
                      boxShadow: "0 4px 8px rgba(0,0,0,0.2)",
                      transition: { type: "spring", stiffness: 400, damping: 10 }
                    }}
                    sx={{
                      color: "white",
                      bgcolor: socialColors[social.name],
                      width: { xs: 36, sm: 40 },
                      height: { xs: 36, sm: 40 },
                      "&:hover": {
                        bgcolor: socialColors[social.name],
                      },
                      transition: "all 0.2s ease",
                    }}
                  >
                    {social.icon}
                  </IconButton>
                ))}
              </Stack>
            </Grid>

            {/* Sayfalar - Mobil düzeltilmiş */}
            <Grid
              item
              xs={6}
              sm={6}
              md={2}
              sx={{
                pl: { xs: 2, sm: 3 },
              }}
            >
              <Typography
                variant="h6"
                fontWeight={600}
                mb={{ xs: 2, md: 3 }}
                sx={{
                  position: "relative",
                  fontSize: { xs: "1rem", sm: "1.1rem" },
                  pb: 1,
                  color: headerColor,
                  "&:after": {
                    content: '""',
                    position: "absolute",
                    bottom: 0,
                    left: 0,
                    width: 40,
                    height: 2,
                    backgroundColor: headerColor,
                  },
                }}
              >
                {i18n.language === "tr" ? "Sayfalar" : "Pages"}
              </Typography>

              <Box
                component="nav"
                sx={{ display: "flex", flexDirection: "column" }}
              >
                {navLinks1.map((item) => (
                  <Box
                    key={item.name}
                    component={motion.div}
                    whileHover={{ 
                      x: 8, 
                      transition: { type: "spring", stiffness: 400, damping: 10 }
                    }}
                    sx={{ mb: 1.5 }}
                  >
                    <Link
                      component="button"
                      onClick={() => handleNavigate(item.path)}
                      sx={{
                        color: alpha("#fff", 0.8),
                        display: "flex",
                        alignItems: "center",
                        textDecoration: "none",
                        fontSize: { xs: "0.85rem", sm: "0.9rem" },
                        transition: "color 0.2s ease",
                        "&:hover": {
                          color: "white",
                        },
                      }}
                    >
                      <KeyboardArrowRightIcon sx={{ mr: 0.5, fontSize: { xs: 14, sm: 16 } }} />
                      {item.name}
                    </Link>
                  </Box>
                ))}
              </Box>
            </Grid>

            {/* Ürünlerimiz Sütun 1 - Mobil düzeltilmiş */}
            <Grid
              item
              xs={6}
              sm={3}
              md={2}
              sx={{ 
                pl: { xs: 2, sm: 3 }
              }}
            >
              <Typography
                variant="h6"
                fontWeight={600}
                mb={{ xs: 2, md: 3 }}
                sx={{
                  position: "relative",
                  fontSize: { xs: "1rem", sm: "1.1rem" },
                  pb: 1,
                  color: headerColor,
                  "&:after": {
                    content: '""',
                    position: "absolute",
                    bottom: 0,
                    left: 0,
                    width: { xs: 40, sm: 60 },
                    height: 2,
                    backgroundColor: headerColor,
                  },
                }}
              >
                {i18n.language === "tr" ? "Ürünlerimiz" : "Our Products"}
              </Typography>

              <Box
                component="nav"
                sx={{ display: "flex", flexDirection: "column" }}
              >
                {productItemsColumn1.map((item) => (
                  <Box
                    key={item.path}
                    component={motion.div}
                    whileHover={{ 
                      x: 8, 
                      transition: { type: "spring", stiffness: 400, damping: 10 }
                    }}
                    sx={{ mb: 1.5 }}
                  >
                    <Link
                      component="button"
                      onClick={() => handleNavigate(item.path)}
                      sx={{
                        color: alpha("#fff", 0.8),
                        display: "flex",
                        alignItems: "center",
                        textDecoration: "none",
                        fontSize: { xs: "0.85rem", sm: "0.9rem" },
                        transition: "color 0.2s ease",
                        "&:hover": {
                          color: "white",
                        },
                      }}
                    >
                      <KeyboardArrowRightIcon sx={{ mr: 0.5, fontSize: { xs: 14, sm: 16 } }} />
                      {i18n.language === "tr" ? item.name.tr : item.name.en}
                    </Link>
                  </Box>
                ))}
              </Box>
            </Grid>

            {/* Ürünlerimiz Sütun 2 - Sadece tablet ve desktop görünümü */}
            <Grid
              item
              xs={6}
              sm={3}
              md={2}
              sx={{ 
                display: { xs: 'none', sm: 'block' },
                pl: { sm: 3 }
              }}
            >
              <Typography
                variant="h6"
                fontWeight={600}
                mb={{ xs: 2, md: 3 }}
                sx={{
                  position: "relative",
                  fontSize: { xs: "1rem", sm: "1.1rem" },
                  pb: 1,
                  color: headerColor,
                  visibility: "hidden",
                }}
              >
                {i18n.language === "tr" ? "Ürünlerimiz" : "Our Products"}
              </Typography>

              <Box
                component="nav"
                sx={{ display: "flex", flexDirection: "column" }}
              >
                {productItemsColumn2.map((item) => (
                  <Box
                    key={item.path}
                    component={motion.div}
                    whileHover={{ 
                      x: 8, 
                      transition: { type: "spring", stiffness: 400, damping: 10 }
                    }}
                    sx={{ mb: 1.5 }}
                  >
                    <Link
                      component="button"
                      onClick={() => handleNavigate(item.path)}
                      sx={{
                        color: alpha("#fff", 0.8),
                        display: "flex",
                        alignItems: "center",
                        textDecoration: "none",
                        fontSize: { xs: "0.85rem", sm: "0.9rem" },
                        transition: "color 0.2s ease",
                        "&:hover": {
                          color: "white",
                        },
                      }}
                    >
                      <KeyboardArrowRightIcon sx={{ mr: 0.5, fontSize: { xs: 14, sm: 16 } }} />
                      {i18n.language === "tr" ? item.name.tr : item.name.en}
                    </Link>
                  </Box>
                ))}
              </Box>
            </Grid>

            {/* 2. Ürünler Sütunu - Sadece Mobil için (xs ekranlar) */}
            <Grid
              item
              xs={6}
              sx={{ 
                display: { xs: 'block', sm: 'none' },
                pl: { xs: 2 } 
              }}
            >
              <Typography
                variant="h6"
                fontWeight={600}
                mb={{ xs: 2 }}
                sx={{
                  position: "relative",
                  fontSize: { xs: "1rem" },
                  pb: 1,
                  color: headerColor,
                  "&:after": {
                    content: '""',
                    position: "absolute",
                    bottom: 0,
                    left: 0,
                    width: 40,
                    height: 2,
                    backgroundColor: headerColor,
                  },
                }}
              >
                {i18n.language === "tr" ? "Diğer Ürünler" : "More Products"}
              </Typography>

              <Box
                component="nav"
                sx={{ display: "flex", flexDirection: "column" }}
              >
                {productItemsColumn2.map((item) => (
                  <Box
                    key={item.path}
                    component={motion.div}
                    whileHover={{ 
                      x: 8, 
                      transition: { type: "spring", stiffness: 400, damping: 10 }
                    }}
                    sx={{ mb: 1.5 }}
                  >
                    <Link
                      component="button"
                      onClick={() => handleNavigate(item.path)}
                      sx={{
                        color: alpha("#fff", 0.8),
                        display: "flex",
                        alignItems: "center",
                        textDecoration: "none",
                        fontSize: { xs: "0.85rem" },
                        transition: "color 0.2s ease",
                        "&:hover": {
                          color: "white",
                        },
                      }}
                    >
                      <KeyboardArrowRightIcon sx={{ mr: 0.5, fontSize: { xs: 14 } }} />
                      {i18n.language === "tr" ? item.name.tr : item.name.en}
                    </Link>
                  </Box>
                ))}
              </Box>
            </Grid>

            {/* İletişim Bilgileri - Mobil optimizasyon */}
            <Grid
              item
              xs={12}
              sm={6}
              md={3}
              sx={{
                pl: { xs: 4, sm: 3 },
                pr: { xs: 4, sm: 3 },
                mt: { xs: 3, sm: 0 }
              }}
            >
              <Typography
                variant="h6"
                fontWeight={600}
                mb={{ xs: 2, md: 3 }}
                sx={{
                  position: "relative",
                  fontSize: { xs: "1rem", sm: "1.1rem" },
                  pb: 1,
                  color: headerColor,
                  textAlign: { xs: 'left', sm: 'left' },
                  "&:after": {
                    content: '""',
                    position: "absolute",
                    bottom: 0,
                    left: 0,
                    width: 40,
                    height: 2,
                    backgroundColor: headerColor,
                  },
                }}
              >
                {t("footer.contact")}
              </Typography>

              <Box sx={{ mb: 2 }}>
                <Box
                  component={motion.div}
                  whileHover={{
                    x: 5,
                    transition: { type: "spring", stiffness: 400, damping: 15 }
                  }}
                  sx={{
                    display: "flex",
                    mb: 2,
                    "&:hover": {
                      "& svg": {
                        color: headerColor,
                      },
                    },
                  }}
                >
                  <LocationOnIcon
                    sx={{
                      mr: { xs: 1.5, sm: 2 },
                      color: alpha("#fff", 0.6),
                      fontSize: { xs: 18, sm: 20 },
                      flexShrink: 0,
                      transition: "color 0.2s ease",
                      mt: 0.2,
                    }}
                  />
                  <Typography
                    variant="body2"
                    sx={{
                      lineHeight: 1.6,
                      color: alpha("#fff", 0.8),
                      fontSize: { xs: "0.8rem", sm: "0.85rem" },
                    }}
                  >
                    {i18n.language === "tr"
                      ? COMPANY_INFO.address.tr
                      : COMPANY_INFO.address.en}
                  </Typography>
                </Box>

                <Box
                  component={motion.div}
                  whileHover={{
                    x: 5,
                    transition: { type: "spring", stiffness: 400, damping: 15 }
                  }}
                  sx={{
                    display: "flex",
                    mb: 2,
                    alignItems: "center",
                    "&:hover": {
                      "& svg": {
                        color: headerColor,
                      },
                    },
                  }}
                >
                  <EmailIcon
                    sx={{
                      mr: { xs: 1.5, sm: 2 },
                      color: alpha("#fff", 0.6),
                      fontSize: { xs: 18, sm: 20 },
                      flexShrink: 0,
                      transition: "color 0.2s ease",
                    }}
                  />
                  <Link
                    href="mailto:info@sgttr.com"
                    sx={{
                      color: alpha("#fff", 0.8),
                      textDecoration: "none",
                      transition: "color 0.2s ease",
                      fontSize: { xs: "0.8rem", sm: "0.85rem" },
                      "&:hover": {
                        color: "white",
                      },
                    }}
                  >
                    info@sgttr.com
                  </Link>
                </Box>

                <Box
                  component={motion.div}
                  whileHover={{
                    x: 5,
                    transition: { type: "spring", stiffness: 400, damping: 15 }
                  }}
                  sx={{
                    display: "flex",
                    mb: 2,
                    alignItems: "center",
                    "&:hover": {
                      "& svg": {
                        color: headerColor,
                      },
                    },
                  }}
                >
                  <PhoneIcon
                    sx={{
                      mr: { xs: 1.5, sm: 2 },
                      color: alpha("#fff", 0.6),
                      fontSize: { xs: 18, sm: 20 },
                      flexShrink: 0,
                      transition: "color 0.2s ease",
                    }}
                  />
                  <Link
                    href="tel:+905333992188"
                    sx={{
                      color: alpha("#fff", 0.8),
                      textDecoration: "none",
                      transition: "color 0.2s ease",
                      fontSize: { xs: "0.8rem", sm: "0.85rem" },
                      "&:hover": {
                        color: "white",
                      },
                    }}
                  >
                    +90 533 399 2188
                  </Link>
                </Box>

                <Box
                  component={motion.div}
                  whileHover={{
                    x: 5,
                    transition: { type: "spring", stiffness: 400, damping: 15 }
                  }}
                  sx={{
                    display: "flex",
                    mb: 2,
                    "&:hover": {
                      "& svg": {
                        color: headerColor,
                      },
                    },
                  }}
                >
                  <AccessTimeIcon
                    sx={{
                      mr: { xs: 1.5, sm: 2 },
                      color: alpha("#fff", 0.6),
                      fontSize: { xs: 18, sm: 20 },
                      flexShrink: 0,
                      transition: "color 0.2s ease",
                      mt: 0.2,
                    }}
                  />
                  <Typography
                    variant="body2"
                    sx={{
                      color: alpha("#fff", 0.8),
                      fontSize: { xs: "0.8rem", sm: "0.85rem" },
                    }}
                  >
                    {i18n.language === "tr"
                      ? COMPANY_INFO.workingHours.tr
                      : COMPANY_INFO.workingHours.en}
                  </Typography>
                </Box>
              </Box>
            </Grid>
          </Grid>

          {/* Alt kısım dekoratif unsur - Altın rengi çizgi deseni */}
          <Box
            sx={{
              position: "absolute",
              bottom: 35,
              left: 0,
              right: 0,
              height: "30px",
              opacity: 0.1,
              zIndex: 0,
              display: { xs: "none", md: "block" },
              "&::before": {
                content: '""',
                position: "absolute",
                bottom: 0,
                left: 0,
                right: 0,
                height: "30px",
                backgroundImage: `
                  repeating-linear-gradient(
                    90deg,
                    transparent,
                    transparent 10px,
                    rgba(243, 156, 18, 0.5) 10px,
                    rgba(243, 156, 18, 0.5) 12px,
                    transparent 12px,
                    transparent 25px
                  )
                `,
              },
            }}
          />

          {/* Tarım temalı divider - mobil optimizasyon */}
          <Box
            sx={{
              position: "relative",
              my: { xs: 2, sm: 3 },
              height: "2px",
              backgroundColor: alpha("#fff", 0.05),
              zIndex: 1,
              "&::before": {
                content: '""',
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                width: { xs: "40px", md: "80px" },
                height: { xs: "10px", md: "15px" },
                backgroundColor: "#2c3e50",
                zIndex: 2,
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              },
              "&::after": {
                content: { xs: 'none', md: '""' }, // Mobilde gösterme
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%) rotate(45deg)",
                width: "10px",
                height: "10px",
                border: `2px solid ${alpha("#f39c12", 0.5)}`,
                borderRadius: "2px",
                zIndex: 3,
              },
            }}
          />

          {/* Copyright - Mobil optimizasyon */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              position: "relative",
              zIndex: 1,
            }}
          >
            <Typography
              variant="body2"
              color={alpha("#fff", 0.6)}
              sx={{
                fontSize: { xs: "0.7rem", sm: "0.75rem" },
                textAlign: "center",
                py: 1,
                letterSpacing: 0.5,
              }}
            >
              © {currentYear} SAFE GLOBAL TRADE.{" "}
              {i18n.language === "tr"
                ? "Tüm Hakları Saklıdır."
                : "All Rights Reserved."}
            </Typography>
          </Box>
        </Container>
      </Box>
    </>
  );
};

export default Footer;